import React from "react";

/**
 * Therapist portrait component.
 *
 * Renders a real photo if `therapist.photoUrl` is set, otherwise falls
 * back to an abstract SVG illustration keyed off `portraitColors` and
 * `slug`. This means you can add photos one therapist at a time without
 * breaking the ones that don't have one yet.
 *
 * Three sizes:
 *   <TherapistPortrait therapist={t} size="card" />     — 200x260, listing
 *   <TherapistPortrait therapist={t} size="profile" />  — 300x380, detail page
 *   <TherapistPortrait therapist={t} size="mini" />     — 200x200, homepage
 */

// Each illustration variant — keyed off therapist slug for consistency
const VARIANTS = {
    "maya-reyes": "warm-curves",
    "david-park": "geometric-calm",
    "noor-haddad": "layered-arch",
    "alex-rivera": "soft-prism",
    "rachel-cohen": "organic-light",
    "marcus-wright": "anchored-frame",
    "priya-iyer": "structured-flow",
    "sam-okonkwo": "open-bloom",
};

function Composition({ variant, c1, c2, slug }) {
    const gradId = `port-${slug}`;
    const accentId = `port-acc-${slug}`;

    const defs = (
        <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={c1} stopOpacity="0.95" />
                <stop offset="100%" stopColor={c2} stopOpacity="0.85" />
            </linearGradient>
            <linearGradient id={accentId} x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor={c2} stopOpacity="0.4" />
                <stop offset="100%" stopColor="white" stopOpacity="0.3" />
            </linearGradient>
        </defs>
    );

    // viewBox is always 200x200 for `card`/`mini`, 300x380 for `profile`
    // Each variant works at both. Coordinates assume 200x200 baseline.

    switch (variant) {
        case "warm-curves":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Sun-like circle */}
                    <circle cx="100" cy="70" r="38" fill="white" opacity="0.22" />
                    <circle cx="100" cy="70" r="24" fill="white" opacity="0.18" />
                    {/* Soft rolling hills */}
                    <path d="M0,140 Q50,115 100,135 T200,130 L200,200 L0,200 Z" fill="white" opacity="0.18" />
                    <path d="M0,160 Q50,145 100,160 T200,155 L200,200 L0,200 Z" fill="white" opacity="0.22" />
                </>
            );
        case "geometric-calm":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Stacked rectangles — solid, structured */}
                    <rect x="40" y="50" width="120" height="50" fill="white" opacity="0.18" rx="3" />
                    <rect x="60" y="105" width="100" height="40" fill="white" opacity="0.22" rx="3" />
                    <rect x="80" y="150" width="60" height="30" fill="white" opacity="0.16" rx="3" />
                    {/* Vertical line accent */}
                    <line x1="30" y1="40" x2="30" y2="180" stroke="white" strokeWidth="1.5" opacity="0.3" />
                </>
            );
        case "layered-arch":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Concentric arches — depth, layers */}
                    <path d="M30,200 Q30,80 100,80 Q170,80 170,200" fill="none" stroke="white" strokeWidth="1.5" opacity="0.32" />
                    <path d="M50,200 Q50,100 100,100 Q150,100 150,200" fill="none" stroke="white" strokeWidth="1.5" opacity="0.28" />
                    <path d="M70,200 Q70,120 100,120 Q130,120 130,200" fill="none" stroke="white" strokeWidth="1.5" opacity="0.24" />
                    <circle cx="100" cy="60" r="14" fill="white" opacity="0.3" />
                </>
            );
        case "soft-prism":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Triangular shapes — angles, identity */}
                    <polygon points="100,40 160,140 40,140" fill="white" opacity="0.15" />
                    <polygon points="100,70 140,150 60,150" fill="white" opacity="0.22" />
                    <polygon points="100,100 120,160 80,160" fill="white" opacity="0.28" />
                </>
            );
        case "organic-light":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Overlapping organic shapes — softness, holding */}
                    <ellipse cx="80" cy="90" rx="55" ry="40" fill="white" opacity="0.18" />
                    <ellipse cx="125" cy="120" rx="50" ry="38" fill="white" opacity="0.22" />
                    <ellipse cx="100" cy="160" rx="40" ry="20" fill="white" opacity="0.16" />
                </>
            );
        case "anchored-frame":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Strong horizontal bands — grounded, anchored */}
                    <rect x="0" y="60" width="200" height="3" fill="white" opacity="0.35" />
                    <rect x="0" y="100" width="200" height="6" fill="white" opacity="0.28" />
                    <rect x="0" y="140" width="200" height="3" fill="white" opacity="0.35" />
                    <rect x="60" y="80" width="80" height="40" fill="white" opacity="0.18" rx="2" />
                </>
            );
        case "structured-flow":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Diagonal grid lines — organized, focused */}
                    <g stroke="white" strokeWidth="1" opacity="0.28">
                        <line x1="0" y1="60" x2="200" y2="40" />
                        <line x1="0" y1="100" x2="200" y2="80" />
                        <line x1="0" y1="140" x2="200" y2="120" />
                        <line x1="0" y1="180" x2="200" y2="160" />
                    </g>
                    <circle cx="140" cy="80" r="22" fill="white" opacity="0.22" />
                </>
            );
        case "open-bloom":
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    {/* Radiating petals — opening, becoming */}
                    <g opacity="0.22" fill="white">
                        <ellipse cx="100" cy="60" rx="14" ry="38" />
                        <ellipse cx="100" cy="60" rx="14" ry="38" transform="rotate(60 100 100)" />
                        <ellipse cx="100" cy="60" rx="14" ry="38" transform="rotate(120 100 100)" />
                        <ellipse cx="100" cy="60" rx="14" ry="38" transform="rotate(180 100 100)" />
                        <ellipse cx="100" cy="60" rx="14" ry="38" transform="rotate(240 100 100)" />
                        <ellipse cx="100" cy="60" rx="14" ry="38" transform="rotate(300 100 100)" />
                    </g>
                    <circle cx="100" cy="100" r="14" fill="white" opacity="0.4" />
                </>
            );
        default:
            return (
                <>
                    {defs}
                    <rect width="100%" height="100%" fill={`url(#${gradId})`} />
                    <circle cx="100" cy="100" r="40" fill="white" opacity="0.25" />
                </>
            );
    }
}

function TherapistPortrait({ therapist, size = "card", className = "" }) {
    // Real photo takes priority over the SVG illustration.
    if (therapist.photoUrl) {
        return (
            <img
                src={therapist.photoUrl}
                alt={therapist.name}
                className={`therapist-portrait-photo ${className}`}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                }}
            />
        );
    }

    const [c1, c2] = therapist.portraitColors;
    const variant = VARIANTS[therapist.slug] || "warm-curves";

    // viewBox sized to match container aspect ratio so the illustration
    // doesn't crop awkwardly. card → portrait 3:4, profile → portrait 3:4ish,
    // mini → square (rendered tiny in a circle, doesn't matter).
    const viewBox =
        size === "profile" ? "0 0 300 380"
        : size === "card" ? "0 0 200 260"
        : "0 0 200 200";

    return (
        <svg
            viewBox={viewBox}
            xmlns="http://www.w3.org/2000/svg"
            className={`therapist-portrait-svg ${className}`}
            preserveAspectRatio="xMidYMid slice"
            aria-label={`Portrait of ${therapist.name}`}
        >
            {size === "profile" ? (
                // Stretched composition for taller aspect ratio
                <g transform="scale(1.5, 1.9)">
                    <Composition variant={variant} c1={c1} c2={c2} slug={therapist.slug} />
                </g>
            ) : size === "card" ? (
                // Stretched vertically for portrait card
                <g transform="scale(1, 1.3)">
                    <Composition variant={variant} c1={c1} c2={c2} slug={therapist.slug} />
                </g>
            ) : (
                <Composition variant={variant} c1={c1} c2={c2} slug={therapist.slug} />
            )}
        </svg>
    );
}

/**
 * Tiny circular avatar for use in nav, banners, mini cards.
 * Uses the real photo if available, otherwise falls back to a
 * CSS gradient + initials.
 */
export function TherapistAvatar({ therapist, size = 48, className = "" }) {
    const initials = therapist.name
        .replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s+/, "")
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("");

    const baseStyle = {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        flexShrink: 0,
    };

    if (therapist.photoUrl) {
        return (
            <img
                src={therapist.photoUrl}
                alt={`${therapist.name} avatar`}
                className={`therapist-avatar ${className}`}
                style={{
                    ...baseStyle,
                    objectFit: "cover",
                    display: "block",
                }}
            />
        );
    }

    return (
        <div
            className={`therapist-avatar ${className}`}
            style={{
                ...baseStyle,
                background: `linear-gradient(135deg, ${therapist.portraitColors[0]}, ${therapist.portraitColors[1]})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: 600,
                fontSize: `${size * 0.32}px`,
                letterSpacing: "0.02em",
            }}
            aria-label={`${therapist.name} avatar`}
        >
            {initials}
        </div>
    );
}

export default TherapistPortrait;